import { Badge } from 'src/design/atoms/Badge';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { IconBrain } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatMemoryQuery } from 'src/api/queries/chat/useChatMemoryQueries';
import { MEMORY_PROFILE_PATH } from 'src/app/chat/memoryNotes';
import { useChatUi } from 'src/hooks/useChat';

export interface IUsedMemoryChipProps {
  usedMemoryIds: number[];
}

/** @registry "Used memory" chip in an assistant reply footer: resolves the reply's used memory note ids against the user's memory list, lists their texts in its tooltip and accessible name, and opens the profile memory section on click; renders nothing when none of the notes still exist. */
export const UsedMemoryChip: React.FC<IUsedMemoryChipProps> = ({
  usedMemoryIds,
}) => {
  const navigate = useNavigate();
  const { closeDrawer } = useChatUi();
  const { data } = useChatMemoryQuery(usedMemoryIds.length > 0);
  const notes = useMemo(
    () =>
      (data?.notes ?? []).filter(
        (note) => note.status === 'active' && usedMemoryIds.includes(note.id),
      ),
    [data, usedMemoryIds],
  );
  if (notes.length === 0) return null;

  return (
    <Tooltip
      multiline
      w={300}
      position="top-start"
      withinPortal
      label={
        <Stack gap={2}>
          {notes.map((note) => (
            <Text key={note.id} size="xs" c="white">
              {note.text}
            </Text>
          ))}
        </Stack>
      }
    >
      <Badge
        component="button"
        type="button"
        size="sm"
        variant="light"
        color="blue"
        aria-label={`Used memory: ${notes
          .map((note) => note.text)
          .join('; ')}. Open memory settings.`}
        leftSection={<IconBrain size={12} aria-hidden />}
        style={{
          alignSelf: 'flex-start',
          cursor: 'pointer',
          textTransform: 'none',
        }}
        onClick={() => {
          closeDrawer();
          navigate(MEMORY_PROFILE_PATH);
        }}
      >
        Used memory
      </Badge>
    </Tooltip>
  );
};
